import { PusherEvent } from '@mondaygpt/constants'
import { useListenToPusherEvent } from 'modules/core/hooks/useListenToPusherEvent'
import { useCallback } from 'react'
import { useWeeklyQueueStore } from 'modules/autopilot/store/weekly-queue-store'
import { useCreativeStore } from '../store/creative-store'
import { useIdeaStore } from '../store/idea-store'
import { usePublishStatusStore } from '../store/publish-status-store'
import { useTopicStore } from '../store/topic-store'

interface PostReadyData {
  projectId: string
  primaryCreativeId: string
  type: string
  shape: string
  lens: string
}

interface PostTopicCompleteData {
  businessId: string
}

interface AutopilotCreativeReadyData {
  creativeId: string
  type: string
  autopilotId: string
}

interface AutopilotGenerationCompleteData {
  businessId: string
  autopilotId: string
}

interface CreativePublishStartedData {
  creativeId: string
  platform: string
  /**
   * Server-side emit time (epoch ms). The processor fires `started` without
   * awaiting it, so on a near-instant failure `complete` can reach Pusher
   * first — this timestamp lets the store drop the late `started` instead of
   * resurrecting the publishing spinner. Optional: absent on events from
   * emitters that predate it.
   */
  emittedAt?: number
}

/**
 * The shape a `PusherEvent.CreativePublishComplete` payload always carries.
 * The publish services also emit platform-specific id keys
 * (`tweetId`, `videoId`, `permalink`) — those live on the ChatPusherHandler-
 * side extension of this type (see below), because THIS hook only needs the
 * status fields.
 */
export interface CreativePublishCompleteData {
  creativeId: string
  platform: string
  success: boolean
  publishId?: string
  error?: string
  /**
   * Machine failure token from the server classifier (e.g. `token_expired`,
   * `image_error`, `app_unaudited`). Present on failure events only.
   * Feed to `getPublishFailureCopy` for humanized card copy.
   */
  failureReason?: string
  retryable?: boolean
  /** Server-side emit time (epoch ms) — see CreativePublishStartedData. */
  emittedAt?: number
}

interface CreativeContentUpdatedData {
  creativeId: string
  /** Absent when the server truncated the payload (see `_truncated`). */
  fields?: Record<string, unknown>
  /**
   * Set by the server when the patch exceeded the Pusher cap (every carousel
   * deck). ChatPusherHandler owns the refetch and mirrors the full content into
   * the idea store itself.
   */
  _truncated?: boolean
}

interface TopicReadyData {
  topicId: string
  replacesTopicId?: string
}

interface TopicCreativesReadyData {
  topicId: string
}

/**
 * Listens to Pusher events for real-time idea (Post-as-cluster) and autopilot updates.
 * Mount once in AuthAppLayout.
 */
export function usePostPusher() {
  const setIdeaReady = useIdeaStore(s => s.setIdeaReady)
  const setGenerationComplete = useIdeaStore(s => s.setGenerationComplete)
  const patchCreativeContent = useIdeaStore(s => s.patchCreativeContent)
  const fetchCreatives = useCreativeStore(s => s.fetchCreatives)
  const markItemPublished = useWeeklyQueueStore(s => s.markItemPublished)
  const markItemPublishFailed = useWeeklyQueueStore(s => s.markItemPublishFailed)
  const markCreativePublishing = useIdeaStore(s => s.markCreativePublishing)
  const applyPublishResultToIdea = useIdeaStore(s => s.applyPublishResultToIdea)
  const setPublishing = usePublishStatusStore(s => s.setPublishing)
  const handleTopicReady = useTopicStore(s => s.handleTopicReady)
  const handleTopicCreativesReady = useTopicStore(s => s.handleTopicCreativesReady)

  const handlePostReady = useCallback(
    (data: PostReadyData) => {
      setIdeaReady(data.projectId)
    },
    [setIdeaReady]
  )

  // Background partial-update channel — used by the IdeaCard background video
  // gen flow to deliver `videoUrl` / `videoStatus` after the agent loop has
  // already finished and fired PostReady. Patching content lets AssetMedia
  // swap from poster + play overlay to inline VideoAsset without a full
  // refetch.
  const handleCreativeContentUpdated = useCallback(
    (data: CreativeContentUpdatedData) => {
      // A truncated payload carries no fields: nothing to patch here, the
      // recovery refetch in ChatPusherHandler lands the full content.
      if (data._truncated || !data.fields) return
      patchCreativeContent(data.creativeId, data.fields)
    },
    [patchCreativeContent]
  )

  const handlePostTopicComplete = useCallback(
    (_data: PostTopicCompleteData) => {
      setGenerationComplete()
    },
    [setGenerationComplete]
  )

  const handleAutopilotCreativeReady = useCallback(
    (_data: AutopilotCreativeReadyData) => {
      fetchCreatives()
    },
    [fetchCreatives]
  )

  const handleAutopilotGenerationComplete = useCallback(
    (_data: AutopilotGenerationCompleteData) => {
      fetchCreatives()
    },
    [fetchCreatives]
  )

  // Drives the cockpit's "post went live" acid pulse the moment the
  // publish actually completes, instead of waiting for the next queue
  // refetch. On failure, flip the card/pill to `failed` so the failure
  // chip appears live (Phase 4 of the publish-failure-recovery feature).
  // ChatPusherHandler owns the parallel usePublishStatusStore update for
  // both success and failure — don't duplicate it here.
  const handlePublishComplete = useCallback(
    (data: CreativePublishCompleteData) => {
      if (data.success) {
        markItemPublished(data.creativeId)
      } else {
        markItemPublishFailed(data.creativeId, data.failureReason)
      }
      // Also patch the idea store so an OPEN PostModal reflects the result in
      // place (failed → live on success, or the fresh failure reason) instead
      // of staying stale until a reopen.
      applyPublishResultToIdea(data.creativeId, {
        success: data.success,
        failureReason: data.failureReason,
        error: data.error,
        emittedAt: data.emittedAt,
      })
    },
    [markItemPublished, markItemPublishFailed, applyPublishResultToIdea]
  )

  // Autopilot's scheduled-publish processor emits CreativePublishStarted so
  // an in-flight autopilot publish surfaces the same "publishing…" state as
  // a manual publish (the failure-recovery feature requires re-attempts to
  // visibly run the same process as the first publish — see plan). Manual
  // publishes already call setPublishing directly from the tap handler; the
  // event handler is idempotent so a double-fire is fine.
  const handlePublishStarted = useCallback(
    (data: CreativePublishStartedData) => {
      setPublishing(data.creativeId, data.platform)
      // Flag the creative mid-publish so the open PostModal shows a "publishing…"
      // spinner on that platform row (mirrors the manual publish process).
      // `emittedAt` lets the store drop a late-delivered `started` that would
      // otherwise land AFTER its own `complete` and stick the spinner forever.
      markCreativePublishing(data.creativeId, true, data.emittedAt)
    },
    [setPublishing, markCreativePublishing]
  )

  // Post idea events. Subscribe to the new event names; the server dual-emits
  // the legacy PostSuggestion* names through 2026-05-17 for clients on the
  // pre-rename bundle.
  useListenToPusherEvent<PostReadyData>(PusherEvent.PostReady, handlePostReady)

  useListenToPusherEvent<PostTopicCompleteData>(
    PusherEvent.PostTopicComplete,
    handlePostTopicComplete
  )

  useListenToPusherEvent<CreativeContentUpdatedData>(
    PusherEvent.CreativeContentUpdated,
    handleCreativeContentUpdated
  )

  // Autopilot events
  useListenToPusherEvent<AutopilotCreativeReadyData>(
    PusherEvent.AutopilotCreativeReady,
    handleAutopilotCreativeReady
  )

  useListenToPusherEvent<AutopilotGenerationCompleteData>(
    PusherEvent.AutopilotGenerationComplete,
    handleAutopilotGenerationComplete
  )

  // Publish events (also bound in ChatPusherHandler for the creative
  // store; the cockpit needs its own queue-store update so the acid
  // pulse fires on the QueueCard the instant the publish lands).
  useListenToPusherEvent<CreativePublishStartedData>(
    PusherEvent.CreativePublishStarted,
    handlePublishStarted
  )
  useListenToPusherEvent<CreativePublishCompleteData>(
    PusherEvent.CreativePublishComplete,
    handlePublishComplete
  )

  // Topic-first home events.
  useListenToPusherEvent<TopicReadyData>(PusherEvent.TopicReady, handleTopicReady)
  useListenToPusherEvent<TopicCreativesReadyData>(
    PusherEvent.TopicCreativesReady,
    handleTopicCreativesReady
  )
}
